/**
 * Обложка карточки объекта: цвет по коду и буквы по наименованию.
 *
 * Фотографий у объектов ИД нет и не заводится, поэтому обложка — не картинка, а
 * опознавательный знак: человек возвращается в этот раздел каждый день и ищет
 * глазами один и тот же объект. Работает это цветовым пятном, а не чтением
 * подписей, и цвет обязан быть закреплён за объектом навсегда — отсюда хеш от
 * кода, а не порядковый номер в списке: раздача цветов по позиции меняла бы
 * цвет объекта при заведении соседнего, то есть ломала бы ровно то узнавание,
 * ради которого всё и делается.
 *
 * ## Почему таблица цветов, а не формула
 *
 * Портал смет считает оттенок формулой `hsl(charCodeAt(0) * 37 % 360, 60%, 55%)`
 * (`client/src/components/shared/placeholderCover.tsx`). Здесь она не годится
 * дважды.
 *
 * Во-первых, хеш по ПЕРВОМУ символу: коды объектов одной стройки —
 * `zil18`, `zil19`, `zil27` — получают побайтово один и тот же цвет, то есть
 * знак перестаёт различать ровно те объекты, которые чаще всего путают.
 *
 * Во-вторых, обложка несёт БЕЛЫЕ буквы, то есть является фоном текста, и её
 * контраст обязан проверяться, а не предполагаться. Проверить вычисленный на
 * лету `hsl()` нечем: порог зависит от оттенка нелинейно — при насыщенности
 * 62 % белый держит 4.5:1 до светлоты 54.6 % на красном и всего до 29.7 % на
 * жёлтом. Формула со светлотой 55 % даёт на жёлтом `#d1d147` контраст 1.62:1
 * при требуемых 4.5:1, и это не мелочь на проценты, а разница в три раза.
 *
 * Автоматика такую ошибку не поймает: `linear-gradient` — это
 * `background-image`, правило `color-contrast` в axe возвращает на нём
 * `incomplete`, а гейт §17 смотрит только `violations`. Значит контраст обязан
 * быть заложен в данные и проверен собственным тестом — см. `objectCover.test.ts`.
 *
 * Поэтому цвета лежат таблицей: 16 оттенков с шагом золотого угла (137.5°, при
 * нём соседние клетки далеки по цвету сами по себе) на двух уровнях
 * насыщенности, а светлота КАЖДОЙ клетки подобрана под постоянную
 * относительную яркость — 0.115 у верхнего стопа, 0.062 у нижнего. Все обложки
 * портала поэтому одинаково тёмные и различаются только цветом, а худший
 * контраст белого по всей таблице — 6.16:1 при пороге 4.5:1.
 *
 * ## Совпадения цветов допускаются
 *
 * Тридцать два слота при десятке объектов дают совпадение примерно в девяти
 * случаях из десяти (парадокс дней рождения). Это осознанный размен, а не
 * недосмотр: увеличивать таблицу вредно — при 64 клетках шаг между оттенками
 * падает до 11°, и цвета станут «почти одинаковыми», то есть вернётся та самая
 * проблема, от которой уходим. Совпадение не мешает: у таких объектов
 * различаются инициалы, наименование и код, а цвет работает грубым фильтром
 * «не туда смотрю», а не идентификатором.
 */

/**
 * Пары стопов градиента: `[верхний, нижний]`.
 *
 * Значения правятся только вместе с прогоном `objectCover.test.ts`: тест
 * считает яркость каждого стопа и не пропускает клетку, на которой белый текст
 * опускается ниже 4.5:1.
 */
export const COVER_STOPS: readonly (readonly [string, string])[] = [
  ['#b22a2a', '#8b1818'],
  ['#1a7034', '#0f5223'],
  ['#8a2fca', '#671c9c'],
  ['#6b6219', '#4e470e'],
  ['#1c6978', '#104f5b'],
  ['#ae2970', '#861853'],
  ['#2e6b19', '#1f520f'],
  ['#5049d4', '#2c24cc'],
  ['#954923', '#753515'],
  ['#196b4d', '#0f5239'],
  ['#9f28a9', '#791782'],
  ['#566718', '#3d4a0d'],
  ['#25619d', '#154779'],
  ['#b22a45', '#8b182f'],
  ['#1a7021', '#0f5214'],
  ['#763dd1', '#571fb2'],
  ['#874f4f', '#6c3737'],
  ['#3e6a4b', '#284d33'],
  ['#72518a', '#59396f'],
  ['#645f3a', '#4a4626'],
  ['#40666d', '#2a4a51'],
  ['#844d6a', '#683651'],
  ['#47673c', '#314d28'],
  ['#5c5997', '#43407d'],
  ['#7a5948', '#5e4031'],
  ['#3c6757', '#284d40'],
  ['#7f4d84', '#643668'],
  ['#5b643a', '#424a26'],
  ['#48617a', '#31475e'],
  ['#874f5a', '#6c3742'],
  ['#3e6a42', '#284d2b'],
  ['#6c5591', '#503b72'],
];

/** Запасная пара: недостижима при непустом коде, но снимает `undefined` при индексации. */
const FALLBACK_STOPS: readonly [string, string] = ['#25619d', '#154779'];

/**
 * FNV-1a на 32 битах с финальным перемешиванием.
 *
 * Перемешивание (два сдвига и умножение) добавлено не для красоты: без него
 * различие в одном ПОСЛЕДНЕМ символе оседает в младших битах, а слот берётся из
 * старших. Кодовые точки читаются через `codePointAt`, потому что код объекта по
 * контракту — любые буквы или цифры, включая кириллицу
 * (`objectCodeSchema`, `/^[\p{L}\p{N}]{1,5}$/u`).
 */
function hashCode(code: string): number {
  let hash = 0x811c9dc5;
  for (const ch of code.toLowerCase()) {
    hash ^= ch.codePointAt(0) ?? 0;
    hash = Math.imul(hash, 0x01000193);
  }
  hash ^= hash >>> 15;
  hash = Math.imul(hash, 0x2545f491);
  hash ^= hash >>> 13;
  return hash >>> 0;
}

/**
 * Градиент обложки: детерминирован по коду и переживает переименование объекта.
 *
 * Слот берётся из СТАРШИХ битов (`>>> 27`), а не остатком от деления: у
 * мультипликативного хеша младшие биты перемешаны хуже старших, и на контрольной
 * выборке кодов остаток давал совпадение `zil18` с `msk1`, а сдвиг — ни одного.
 */
export function coverGradient(code: string): string {
  const stops = COVER_STOPS[hashCode(code) >>> 27] ?? FALLBACK_STOPS;
  return `linear-gradient(135deg, ${stops[0]} 0%, ${stops[1]} 100%)`;
}

/**
 * Буквы наименования: первые буквы двух первых слов, иначе две первые буквы.
 *
 * Слова, начинающиеся с цифры, пропускаются: «1-я очередь строительства» это
 * «ЯО», а не «1О» — цифра в качестве опознавательного знака бесполезна, она
 * повторяется у половины объектов.
 */
export function objectInitials(name: string): string {
  const words = name.split(/[^\p{L}\p{N}]+/u).filter((word) => /^\p{L}/u.test(word));
  const [first, second] = words;
  if (first === undefined) {
    const fallback = name.trim().slice(0, 1);
    return fallback === '' ? '?' : fallback.toUpperCase();
  }
  if (second === undefined) return first.slice(0, 2).toUpperCase();
  return `${first.slice(0, 1)}${second.slice(0, 1)}`.toUpperCase();
}
